import React from 'react';
import { Tabs, Table, DatePicker, Button, Typography, Space, Tag } from 'antd';

import request from '../../lib/request';
import { useBooks, BooksSelector, BooksCaption } from '../../lib/books';

import { money } from '../../lib/format';
const { RangePicker } = DatePicker;

/** VAT / VIES / OSS / IOSS returns of one set of books. */
export default function VatReportsPage() {
  const { ledgerId } = useBooks();
  const [range, setRange] = React.useState(null);
  const [vatForm, setVatForm] = React.useState([]);
  const [summary, setSummary] = React.useState({ current: [], previous: [] });
  const [viesForm, setViesForm] = React.useState([]);
  const [ossForm, setOssForm] = React.useState({ UNION: [], NON_UNION: [], IMPORT: [] });
  const [iossForm, setIossForm] = React.useState([]);

  const rangeParams = () => {
    if (!range) return {};
    return { start_date: range[0].format('YYYY-MM-DD'), end_date: range[1].format('YYYY-MM-DD') };
  };

  const runReports = () => {
    if (!ledgerId) return;
    const params = { ledger_id: ledgerId, ...rangeParams() };
    request.get('/vat-reports/form', params).then((r) => r.json()).then((r) => r.status && setVatForm(r.data));
    request.get('/vat-reports/summary', params).then((r) => r.json()).then((r) => r.status && setSummary(r.data));
    request.get('/vat-reports/vies/form', params).then((r) => r.json()).then((r) => r.status && setViesForm(r.data));
    request.get('/vat-reports/oss/form', params).then((r) => r.json()).then((r) => r.status && setOssForm(r.data));
    request.get('/vat-reports/ioss/form', params).then((r) => r.json()).then((r) => r.status && setIossForm(r.data));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(runReports, [ledgerId]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 16 }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            VAT / VIES / OSS / IOSS Reports
          </Typography.Title>
          <BooksCaption />
        </div>
        <Space wrap>
          <BooksSelector />
          <RangePicker onChange={setRange} />
          <Button type="primary" onClick={runReports}>
            Run
          </Button>
        </Space>
      </div>

      <Tabs
        items={[
          {
            key: 'vat-form',
            label: 'VAT Form',
            children: (
              <Table
                rowKey="line_no"
                size="small"
                pagination={false}
                dataSource={vatForm}
                columns={[
                  { title: 'Line', dataIndex: 'line_no', width: 80 },
                  { title: 'Description', dataIndex: 'description' },
                  { title: 'Amount', dataIndex: 'amount', align: 'right', render: (v) => money(v) },
                ]}
              />
            ),
          },
          {
            key: 'vat-summary',
            label: 'VAT Summary',
            children: (
              <Table
                rowKey={(r) => r.vat_code + r.polarity}
                size="small"
                pagination={false}
                dataSource={summary.current}
                columns={[
                  { title: 'VAT Code', dataIndex: 'vat_code', render: (v) => <Tag>{v}</Tag> },
                  { title: 'Name', dataIndex: 'vat_name' },
                  { title: 'Polarity', dataIndex: 'polarity' },
                  { title: 'Net', dataIndex: 'net_amount', align: 'right', render: (v) => money(v) },
                  { title: 'VAT', dataIndex: 'vat_amount', align: 'right', render: (v) => money(v) },
                ]}
              />
            ),
          },
          {
            key: 'vies-form',
            label: 'VIES Form',
            children: (
              <Table
                rowKey="country"
                size="small"
                pagination={false}
                dataSource={viesForm}
                columns={[
                  { title: 'Country', dataIndex: 'country' },
                  { title: 'Goods', dataIndex: 'goods', align: 'right', render: (v) => money(v) },
                  { title: 'Services', dataIndex: 'services', align: 'right', render: (v) => money(v) },
                ]}
              />
            ),
          },
          {
            key: 'oss-form',
            label: 'OSS Form',
            children: (
              <>
                {['UNION', 'NON_UNION', 'IMPORT'].map((scheme) => (
                  <div key={scheme} style={{ marginBottom: 16 }}>
                    <Typography.Text strong>{scheme.replace('_', '-')}</Typography.Text>
                    <Table
                      rowKey="country"
                      size="small"
                      pagination={false}
                      dataSource={ossForm[scheme]}
                      columns={[
                        { title: 'Country', dataIndex: 'country' },
                        { title: 'Amount', dataIndex: 'amount', align: 'right', render: (v) => money(v) },
                      ]}
                    />
                  </div>
                ))}
              </>
            ),
          },
          {
            key: 'ioss-form',
            label: 'IOSS Form',
            children: (
              <Table
                rowKey="country"
                size="small"
                pagination={false}
                dataSource={iossForm}
                columns={[
                  { title: 'Country', dataIndex: 'country' },
                  { title: 'Net', dataIndex: 'net_amount', align: 'right', render: (v) => money(v) },
                  { title: 'VAT', dataIndex: 'vat_amount', align: 'right', render: (v) => money(v) },
                ]}
              />
            ),
          },
        ]}
      />
    </div>
  );
}
